import type { ReactNode } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import type { StyleProp, ViewStyle } from "react-native";
import { semanticColors, typography } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/** The velvet the dock is filled with; web layers a grain and a gradient. */
const DOCK_BACKGROUND = "#3B1A08";
const DOCK_WEB_BACKGROUND = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
  backgroundSize: "200px 200px, 100% 100%",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
};

const CREAM = "#FFF7EF";
const ON_CREAM = "#5A3125";

/**
 * The floating velvet pill that stands in for the tab bar when a screen needs
 * its own actions, such as Clinic Detail (Back, Save, Note, Schedule) or the
 * Care calendar while logging (Cancel, Save). Positioning and any entrance
 * animation stay with the screen; this is only the pill.
 */
export function FloatingActionDock({
  children,
  style,
}: {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <View
      style={[
        {
          flexDirection: "row",
          // Lets the dock narrow inside a phone-width row instead of
          // overhanging the screen; labelled buttons shrink with it.
          flexShrink: 1,
          alignItems: "center",
          gap: 6,
          backgroundColor: DOCK_BACKGROUND,
          borderRadius: 999,
          paddingHorizontal: 6,
          paddingVertical: 6,
          shadowColor: "#1F120C",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.25,
          shadowRadius: 16,
          elevation: 8,
          ...(Platform.OS === "web" ? (DOCK_WEB_BACKGROUND as ViewStyle) : {}),
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

export type FloatingActionDockButtonVariant =
  "default" | "selected" | "frosted";

export type FloatingActionDockButtonProps = {
  /** Visible label. Omit for an icon-only dock button. */
  label?: string;
  accessibilityLabel: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action: string;
  icon?: ReactNode;
  onPress: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  /**
   * `selected` is the cream pill for the chosen or main action; `frosted` is
   * a lit glass pill for the one action that should stand out on the velvet.
   */
  variant?: FloatingActionDockButtonVariant;
  disabled?: boolean;
  /** Share of the dock's width, when the dock is given a fixed width. */
  flex?: number;
};

/** One action inside a `FloatingActionDock`. */
export function FloatingActionDockButton({
  label,
  accessibilityLabel,
  action,
  icon,
  onPress,
  onPressIn,
  onPressOut,
  variant = "default",
  disabled = false,
  flex,
}: FloatingActionDockButtonProps) {
  const capturePress = usePressCapture("FloatingActionDockButton");
  const selected = variant === "selected";
  const frosted = variant === "frosted";
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress();
      }}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected, disabled }}
      style={({ pressed }) => ({
        ...(flex !== undefined && { flex }),
        flexShrink: label ? 1 : 0,
        minHeight: 48,
        paddingHorizontal: 12,
        borderRadius: 23,
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        flexDirection: "row",
        backgroundColor: frosted
          ? semanticColors.onDark.fill.strong
          : selected
            ? CREAM
            : semanticColors.onDark.fill.subtle,
        borderWidth: 1,
        borderColor: frosted
          ? "rgba(255,247,239,0.42)"
          : selected
            ? "rgba(255,247,239,0.94)"
            : semanticColors.onDark.fill.subtle,
        opacity: disabled ? 0.48 : pressed ? 0.72 : 1,
        ...(frosted && Platform.OS === "web"
          ? ({
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
            } as ViewStyle)
          : {}),
      })}
    >
      {icon ? <View style={{ flexShrink: 0 }}>{icon}</View> : null}
      {label ? (
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            flexShrink: 1,
            fontSize: 12,
            lineHeight: 15,
            fontWeight: "900",
            fontFamily: typography.families.sans,
            color: frosted
              ? semanticColors.onDark.text.primary
              : selected
                ? ON_CREAM
                : disabled
                  ? semanticColors.onDark.text.tertiary
                  : semanticColors.onDark.text.primary,
          }}
        >
          {label}
        </Text>
      ) : null}
    </Pressable>
  );
}

export default FloatingActionDock;
